export const parseCardUpgrades = upgradesRaw => {
    if (!upgradesRaw) return {};

    if (typeof upgradesRaw === 'string') {
        try {
            const parsed = JSON.parse(upgradesRaw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
            return {};
        } catch {
            return {};
        }
    }

    if (typeof upgradesRaw === 'object' && !Array.isArray(upgradesRaw)) return upgradesRaw;
    return {};
};

export const getUpgradePoints = (upgrades, key) => {
    const val = upgrades?.[key];
    const num = typeof val === 'number' ? val : parseInt(val, 10);
    return Number.isFinite(num) && num > 0 ? num : 0;
};

export const getUpgradeOptionsForCardType = isBatter => {
    if (isBatter) {
        return [
            { key: 'stamina', label: 'Stamina' },
            { key: 'speed', label: 'Speed' },
            { key: 'power', label: 'Power' },
            { key: 'contact', label: 'Contact' },
            { key: 'superStar', label: 'Super Star' },
            { key: 'drugDealer', label: 'Drug Dealer' },
        ];
    }

    return [
        { key: 'stamina', label: 'Stamina' },
        { key: 'xbhSuppress', label: 'XBH Suppress' },
        { key: 'singleSuppress', label: 'Single Suppress' },
        { key: 'kModifier', label: 'K Modifier' },
        { key: 'fatigueFighter', label: 'Fatigue Fighter' },
        { key: 'superStar', label: 'Super Star' },
        { key: 'drugDealer', label: 'Drug Dealer' },
    ];
};

export const getUpgradeLevel = (upgrades, upgradeOptions) => {
    const fromLevel = upgrades?.level;
    const numLevel = typeof fromLevel === 'number' ? fromLevel : parseInt(fromLevel, 10);
    if (Number.isFinite(numLevel) && numLevel >= 0) return numLevel;

    return (upgradeOptions || []).reduce((sum, opt) => sum + getUpgradePoints(upgrades, opt.key), 0);
};

export const getEffectiveCardStatsFromUpgrades = ({ card, upgrades, globals }) => {
    const isBatter = card?.type === 'Batter';
    const batterEffects = globals?.CARD_UPGRADE_EFFECTS?.batter;
    const pitcherEffects = globals?.CARD_UPGRADE_EFFECTS?.pitcher;

    const speedPoints = isBatter ? getUpgradePoints(upgrades, 'speed') : 0;
    const staminaPoints = getUpgradePoints(upgrades, 'stamina');

    const effectiveBatterSpeed =
        isBatter &&
        card?.speed != null &&
        typeof batterEffects?.speedPerPoint === 'number'
            ? card.speed + speedPoints * batterEffects.speedPerPoint
            : null;
    const effectivePitcherStamina =
        !isBatter &&
        card?.stamina != null &&
        typeof pitcherEffects?.staminaPerPoint === 'number'
            ? card.stamina + staminaPoints * pitcherEffects.staminaPerPoint
            : null;
    const effectiveBatterFatigueThreshold =
        isBatter && globals?.BATTER_FATIGUE_THRESHOLD != null
            && typeof batterEffects?.fatigueThresholdPerPoint === 'number'
            ? globals.BATTER_FATIGUE_THRESHOLD + staminaPoints * batterEffects.fatigueThresholdPerPoint
            : null;

    return {
        speedPoints,
        staminaPoints,
        effectiveBatterSpeed,
        effectivePitcherStamina,
        effectiveBatterFatigueThreshold,
    };
};

export const getUpgradeEffectText = ({
    key,
    points,
    isBatter,
    globals,
    effectiveBatterSpeed,
    effectivePitcherStamina,
    effectiveBatterFatigueThreshold,
}) => {
    const batterEffects = globals?.CARD_UPGRADE_EFFECTS?.batter;
    const pitcherEffects = globals?.CARD_UPGRADE_EFFECTS?.pitcher;

    if (points <= 0) return '—';

    if (isBatter) {
        if (key === 'stamina') {
            if (effectiveBatterFatigueThreshold == null) return '—';
            const games = Math.floor(effectiveBatterFatigueThreshold / 9);
            return `${games} games/day`;
        }
        if (key === 'speed') {
            return effectiveBatterSpeed == null ? '—' : `Speed: ${effectiveBatterSpeed}`;
        }
        if (key === 'power') {
            if (typeof batterEffects?.powerRerollChancePerPointPct !== 'number') return '—';
            const cap = typeof batterEffects?.powerRerollChanceCapPct === 'number' ? batterEffects.powerRerollChanceCapPct : null;
            const chance = points * batterEffects.powerRerollChancePerPointPct;
            return `Reroll chance: ${cap == null ? chance : Math.min(cap, chance)}%`;
        }
        if (key === 'contact') {
            const chance = points * 3;
            return `Reroll chance: ${chance}%`;
        }
        if (key === 'superStar') return `+$${points * 5}/game`;
        if (key === 'drugDealer') return `${Math.min(50, points * 5)}% chance`;
        return '—';
    }

    if (key === 'stamina') return effectivePitcherStamina == null ? '—' : `Stamina: ${effectivePitcherStamina}`;
    if (key === 'xbhSuppress') {
        if (typeof pitcherEffects?.ratingPerPoint !== 'number') return '—';
        return `+${points * pitcherEffects.ratingPerPoint} rating`;
    }
    if (key === 'singleSuppress') {
        if (typeof pitcherEffects?.ratingPerPoint !== 'number') return '—';
        return `+${points * pitcherEffects.ratingPerPoint} rating`;
    }
    if (key === 'kModifier') {
        if (typeof pitcherEffects?.kConvertBasePct !== 'number' || typeof pitcherEffects?.kConvertPerPointPct !== 'number') return '—';
        const cap = typeof pitcherEffects?.kConvertCapPct === 'number' ? pitcherEffects.kConvertCapPct : null;
        const chance = pitcherEffects.kConvertBasePct + points * pitcherEffects.kConvertPerPointPct;
        return `K convert: ${cap == null ? chance : Math.min(cap, chance)}%`;
    }
    if (key === 'fatigueFighter') {
        if (
            typeof pitcherEffects?.fatigueFlipBasePct !== 'number' ||
            typeof pitcherEffects?.fatigueFlipReductionPerPointPct !== 'number'
        ) {
            return '—';
        }
        const reduction = points * pitcherEffects.fatigueFlipReductionPerPointPct;
        return `-${reduction}% fatigue chance`;
    }
    if (key === 'superStar') return `+$${points * 5}/game`;
    if (key === 'drugDealer') return `${Math.min(50, points * 5)}% chance`;
    return '—';
};
